import { ApolloProvider } from '@apollo/client/react';
import { fetchAuthSession } from '@aws-amplify/auth';
import { initializeAuth } from '@choco-admin/auth';
import { initializeApolloClient } from '@choco-admin/graphql-client';
import {
  getCurrentEnvironmentConfig,
  maybeAddGooglePlacesScriptTag,
} from '@choco-admin/utils';
import { HeroUIProvider } from '@heroui/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { GOOGLE_PLACES_API_KEY } from './config';
import { ThemeProvider } from './contexts/ThemeContext';
import { SomethingWentWrongPage } from './pages/SomethingWentWrongPage';
import { setupUrlWatcher } from './utils/urlDebugger';
import './styles/index.css';

// Initialize authentication configuration with dynamic environment
const initializeAuthWithEnvironment = () => {
  try {
    const envConfig = getCurrentEnvironmentConfig();

    // Build redirect URLs - these MUST match what's configured in Cognito App Client
    const redirectSignIn = `${window.location.origin}/`;
    const redirectSignOut = `${window.location.origin}/`;

    initializeAuth({
      region: envConfig.region,
      userPoolId: envConfig.userPoolId,
      userPoolWebClientId: envConfig.userPoolWebClientId,
      identityPoolId: envConfig.identityPoolId,
      // OAuth configuration for Google login
      oauth: {
        domain: envConfig.oauthDomain,
        scope: ['openid', 'email', 'aws.cognito.signin.user.admin'],
        redirectSignIn,
        redirectSignOut,
        responseType: 'code',
      },
    });

    // Store environment config globally for debugging
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__CHOCO_ENV_CONFIG__ = envConfig;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize auth:', error);

    // In development, we might want to continue without auth for testing
    if (process.env.NODE_ENV !== 'development') {
      throw error;
    }
  }
};

// Initialize authentication
initializeAuthWithEnvironment();

// Setup URL debugging to capture OAuth redirects
if (import.meta.env.DEV) {
  setupUrlWatcher();

  // Make URL debugger available globally for manual debugging
  (window as any).debugUrl = () => {
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { captureUrlParams } = require('./utils/urlDebugger');
    return captureUrlParams();
  };
}

const container = document.getElementById('root');
if (!container) throw new Error('Root element not found');

const root = createRoot(container);

// Initialize Apollo Client and render app
async function renderApp() {
  const envConfig = getCurrentEnvironmentConfig();

  // Create auth token getter function
  const getAuthToken = async (): Promise<string | undefined> => {
    try {
      const authSession = await fetchAuthSession();
      return authSession.tokens?.idToken?.toString();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to get auth token:', error);
      return undefined;
    }
  };

  const apolloClient = await initializeApolloClient(envConfig, getAuthToken);

  if (!apolloClient) {
    throw new Error('Failed to initialize Apollo Client');
  }

  root.render(
    <React.StrictMode>
      <ThemeProvider defaultTheme="dark">
        <HeroUIProvider>
          <ApolloProvider client={apolloClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ApolloProvider>
        </HeroUIProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

// Initialize Google Places script - using same pattern as choco-admin-buyers
maybeAddGooglePlacesScriptTag({ apiKey: GOOGLE_PLACES_API_KEY });

// Start the app
renderApp().catch((error) => {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize app:', error);

  // Render error fallback
  root.render(
    <React.StrictMode>
      <SomethingWentWrongPage />
    </React.StrictMode>
  );
});
